import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { AddressUpdateModel } from '../../../shared/services/fn-api/MACService';
import AddressSchema from './AddressSchema';
import { Button, Input, Select } from '@ds';
import styles from './AddressForm.module.scss';
import { useGetCountries } from '../../../shared/queries/useGetCountries';
import { useGetStates } from '../../../shared/queries/useGetStates';
import { Address } from 'src/shared/types/Address';
import { ButtonSize } from '@ds/elements/Button/Button.types';

interface EntityMap<T> {
  [key: string]: T;
}

type AddressFormProps = {
  currentAddress?: Address | null;
  onSubmit: (data?: AddressUpdateModel) => void;
};

const AddressForm: React.FC<AddressFormProps> = ({
  currentAddress,
  onSubmit,
}) => {
  const [address, setAddress] = useState<AddressUpdateModel>({});
  const [formError, setFormError] = useState<EntityMap<boolean>>({});
  
  const { data: countries, isLoading: isLoadingCountries } = useGetCountries();
  const { data: states, isLoading: isLoadingStates } = useGetStates(address?.country || '');
  
  const countryOptions = useMemo(
    () => countries?.map((country) => ({ value: country.countryCode, label: country.countryName })) || [],
    [countries]
  );
  
  const stateOptions = useMemo(
    () => states?.map((state) => ({ value: state.stateName, label: state.stateName })) || [],
    [states]
  );

  const isSubmitable = useMemo(() => {
    // Check for validation errors
    for (const field in formError) {
      if (formError[field]) return false;
    }
    
    // Check required fields are filled
    if (!address.country || !address.addressLine1 || !address.city || !address.zipCode) {
      return false;
    }
    // If country is US, state is required
    if (address.country === 'US' && !address.state) {
      return false;
    }
    
    return true;
  }, [formError, address]);

  useEffect(() => {
    if(currentAddress) {
      validateAddress(currentAddress);
    }
    setAddress({
      ...currentAddress,
      shouldUpdateCurrentResidence: false,
      shouldUpdatePermanentResidence: false,
    });
  }, [currentAddress]);

  const updateAddress = (field: keyof AddressUpdateModel, value: any) => {
    setAddress((prev) => {
      const data = { ...prev, [field]: value };
      if (field === 'country') {
        data.state = '';
        validateAddress(data, 'state');
      }
      validateAddress(data, field);
      return data;
    });
  };

  const validateAddress = (
    data: AddressUpdateModel | undefined,
    field?: keyof AddressUpdateModel
  ) => {
    const { error } = AddressSchema.validate(data, {
      allowUnknown: true,
    });

    if (error?.details.length) {
      if (field) {
        const isError = error.details.some((item) => item.path[0] === field);
        setFormError((prev) => ({
          ...prev,
          [field]: isError,
        }));
      } else {
        const result: EntityMap<boolean> = {};
        error.details.forEach((item) => {
          if (item.path[0]) {
            result[item.path[0]] = true;
          }
        });
        setFormError(result);
      }
      return false;
    }

    setFormError({});
    return true;
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = validateAddress(address);
    isValid && onSubmit(address);
  };

  return (
    <form className={styles['mac-form']} onSubmit={submit} noValidate>
      <div className={`${styles['form-row']} ${styles['form-row-single']}`}>
        <Select
          label="Country / Location"
          required={true}
          placeholder="Select Country"
          options={countryOptions}
          selectedOption={countryOptions.find(option => option.value === address.country) || null}
          onChange={(option) => updateAddress('country', option?.value || '')}
          disabled={isLoadingCountries}
        />
      </div>      
      <div className={`${styles['form-row']} ${styles['form-row-two-half']}`}>
        <Input
          label="Address Line 1"
          required={true}
          type="text"
          name="addressLine1"
          value={address.addressLine1 || ''}
          onChange={(event) => updateAddress('addressLine1', event.target.value)}
          placeholder="Address Line 1"
          showErrorBorder={formError['addressLine1']}
        />
        
        <Input
          label="Address Line 2"
          type="text"
          name="addressLine2"
          value={address.addressLine2 || ''}
          onChange={(event) => updateAddress('addressLine2', event.target.value)}
          placeholder="Address Line 2"
        />
      </div>      
      <div className={`${styles['form-row']} ${styles['form-row-four-equal']}`}>
        <Input
          label="Floor / Apt / Suite"
          type="text"
          name="suite"
          value={address.suite || ''}
          onChange={(event) => updateAddress('suite', event.target.value)}
          placeholder="Floor / Apt / Suite"
        />
        
        <Input
          label="City"
          required={true}
          type="text"
          name="city"
          value={address.city || ''}
          onChange={(event) => updateAddress('city', event.target.value)}
          placeholder="City"
          showErrorBorder={formError['city']}
        />
        
        {stateOptions.length || isLoadingStates ? (
          <Select
            label="State / Province"
            required={address.country === 'US'}
            placeholder="Select State"
            options={stateOptions}
            selectedOption={stateOptions.find(option => option.value === address.state) || null}
            onChange={(option) => updateAddress('state', option?.value || '')}
            disabled={isLoadingStates}
          />
        ) : (
          <Input
            label="State / Province"
            required={address.country === 'US'}
            type="text"
            name="state"
            value={address.state || ''}
            onChange={(event) => updateAddress('state', event.target.value)}
            placeholder="State/Province"
            showErrorBorder={formError['state']}
          />
        )}
        
        <Input
          label="Zip / Postal Code"
          required={true}
          type="text"
          name="zipCode"
          value={address.zipCode || ''}
          onChange={(event) => updateAddress('zipCode', event.target.value)}
          placeholder="Zip/Postal Code"
          showErrorBorder={formError['zipCode']}
        />
      </div>
      
      <div className="actions merged">
        <Button
          styleType='secondary' variant='outlined'
          disabled={!isSubmitable}
          onClick={()=> {onSubmit(address)}}
          size={ButtonSize.large}
        >
          Update Address
        </Button>
      </div>
    </form>
  );
};

export default AddressForm;